import { isEmpty, omit } from 'ramda';
import * as Sentry from '@sentry/react';

import { getEnvironmentType } from '@/helpers/getEnvironmentType';
import { hostname } from '@/config';

const env = getEnvironmentType();
// const isSentryEnabled = window.location.hostname !== 'localhost';
const isSentryEnabled = false;

// TODO: improve config based on
// https://docs.sentry.io/platforms/javascript/
// https://docs.sentry.io/platforms/javascript/guides/react/

interface LogContext {
  [key: string]: any;
  error?: Error;
  message?: never;
}

export class Logger {
  error(message: string, context: LogContext = {}) {
    console.error(message, context);

    sendLog({
      context,
      level: 'error',
      message,
    });
  }

  warn(message: string, context: LogContext = {}) {
    console.warn(message, context);

    sendLog({
      context,
      level: 'warn',
      message,
    });
  }

  log(message: string, context: LogContext = {}) {
    console.log(message, context);
  }

  static init() {
    if (!isSentryEnabled) {
      return;
    }

    Sentry.init({
      /**
       * beforeSend is used to drop noisy events that we don't want in Sentry.
       * We filter:
       *  - Typical Axios network/timeout/cancel cases
       *  - Preact/compat crash when parent container is null (reading '__k')
       */
      beforeSend(event, hint) {
        const message = (
          (event.message as string | undefined) ||
          (event.logentry as any)?.message ||
          (hint?.originalException as any)?.message ||
          (hint?.originalException ? String(hint.originalException) : '')
        ).trim();

        // 1) Drop non-actionable Axios network issues
        const values = event.exception?.values ?? [];
        const isAxiosError = values.some((v) => v.type === 'AxiosError');
        const textDump = [message, ...values.map((v) => `${v.type || ''}: ${v.value || ''}`)].join(
          ' | ',
        );
        if (
          isAxiosError &&
          /Network\s?Error|ECONNABORTED|ERR_(CANCELLED|CANCELED)|timeout|status\s*:\s*(0)/i.test(
            textDump,
          )
        ) {
          return null;
        }

        // 2) Drop Preact internal crash when mounting into null (reading '__k')
        if (/null.*__k/i.test(message)) return null;
        const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
        if (
          frames.some((f) => /(preact\/compat|preact\/dist)/i.test(String(f.filename))) &&
          /__k/.test(textDump)
        ) {
          return null;
        }

        return event; // keep everything else
      },
      denyUrls: [/chrome-extension:\/\//i, /extensions\//i],
      dsn: `https://28b1ff3362d7447fb027cedb605f1052@${hostname}/sentry/104`,
      environment: env,
      /**
       * Coarse-grained filters (Sentry-side). These are backups to beforeSend.
       */
      ignoreErrors: [
        /Network\s?Error/i,
        /ECONNABORTED/i,
        /ERR_(CANCELLED|CANCELED)/i,
        /null.*__k/i,
      ],
      integrations: [
        Sentry.extraErrorDataIntegration({
          depth: 5,
        }),
        Sentry.thirdPartyErrorFilterIntegration({
          behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
          filterKeys: process.env.SENTRY_APP_KEY ? [process.env.SENTRY_APP_KEY] : [],
        }),
      ],
      // normalizeDepth needed for ExtraErrorData.depth to work.
      // It should be depth + 1. More details:
      // https://github.com/getsentry/sentry-javascript/issues/1964#issuecomment-625353135
      normalizeDepth: 6,
      tracesSampleRate: 0,
    });
  }
}

function sendLog({
  level,
  message,
  context = {},
}: {
  level: 'error' | 'warn';
  message: string;
  context: LogContext;
}) {
  if (!isSentryEnabled) {
    return;
  }

  let error: Error | undefined;
  let errorContext: { [key: string]: any } = {};
  let fingerprint: string[] | undefined;

  const { error: sourceError, ...rest } = context;

  if (sourceError) {
    if (sourceError.message === message) {
      error = sourceError;
    } else {
      // if this is a log with custom message, e.g. logger.warn('foo', { error })
      // lets collect another stack trace to show in sentry both the source of
      // the logger call and the source of Error object
      // this also will force sentry to use the `message` as the error description
      // instead of `sourceError.message`
      error = Object.assign(new Error(message), {
        cause: sourceError,
      });

      if (typeof Error.captureStackTrace === 'function') {
        Error.captureStackTrace(error, Logger.prototype[level]);
      }
    }

    const extraProps = omit(['name', 'stack', 'message'], sourceError);

    errorContext = extraProps;
  }

  if (!error) {
    // if we have error instance, let sentry to group events as it wants
    // otherwise let's control grouping based on log params
    fingerprint = [getMessageFingerprint(message), level, env];
  }

  Sentry.captureMessage(
    // @ts-expect-error sentry accepts error in place of message in captureMessage
    error || message,
    {
      extra: {
        ...rest,
        ...(isEmpty(errorContext) ? {} : { errorContext }),
      },
      fingerprint,
      level,
    },
  );
}

function getMessageFingerprint(message: string) {
  // try to filter out json and ids from messages
  const match = message.match(/^([A-Z\s,.:]+)(\s.*|$)/i);

  const matchValue = match?.[1];

  return match && matchValue ? matchValue : 'undefined';
}

export const logger = new Logger();
